import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import type {
  InvestorDetail,
  InvestorDistributionEntry,
  InvestorDistributionStats,
  InvestorParticipation,
  InvestorRelationship,
} from '@/types/investor'

interface Props {
  investor: InvestorDetail
  participations: InvestorParticipation[]
  historical_participations: InvestorParticipation[]
  distribution_stats: InvestorDistributionStats
  recent_distributions: InvestorDistributionEntry[]
}

function availableTaxYears(): number[] {
  const currentYear = new Date().getFullYear()
  return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]
}

const RELATIONSHIP_LABELS: Record<InvestorRelationship, string> = {
  family: 'Family',
  business_partner: 'Business Partner',
  fund_investor: 'Fund Investor',
  other: 'Other',
}

const LOAN_STATUS_BADGES: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  paid_off: { label: 'Paid Off', bg: 'bg-gray-100', text: 'text-gray-600' },
  defaulted: { label: 'Defaulted', bg: 'bg-red-50', text: 'text-red-700' },
  written_off: { label: 'Written Off', bg: 'bg-amber-50', text: 'text-amber-700' },
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function InvestorsShow({
  investor,
  participations,
  historical_participations,
  distribution_stats,
  recent_distributions,
}: Props) {
  const [showHistory, setShowHistory] = useState(false)
  const activeCount = participations.filter((p) => p.loan_status === 'active').length
  const totalDeployed = participations.reduce((sum, p) => sum + p.principal_amount, 0)

  const years = availableTaxYears()
  const [statementYear, setStatementYear] = useState<number>(years[1] ?? years[0])

  function handleDelete() {
    if (!confirm(`Remove ${investor.name}? This will also remove their participations on all loans.`)) {
      return
    }
    router.delete(`/investors/${investor.id}`)
  }

  function handleDownloadStatement() {
    window.location.href = `/investors/${investor.id}/statement?year=${statementYear}`
  }

  function handleInviteToPortal() {
    if (!investor.email) {
      alert('Add an email to this investor before sending a portal invite.')
      return
    }
    const verb = investor.portal_enabled ? 'Re-send' : 'Send'
    if (!confirm(`${verb} a magic-link login email to ${investor.email}?`)) return
    router.post(`/investors/${investor.id}/invite_to_portal`, {}, { preserveScroll: true })
  }

  function formatLastSignIn(iso: string | null) {
    if (!iso) return 'Never signed in'
    const d = new Date(iso)
    return `Last signed in ${d.toLocaleDateString()} at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-1 text-sm text-gray-400">
          <Link href="/investors" className="hover:text-gray-600">Investors</Link>
          <span>/</span>
        </div>
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{investor.name}</h1>
            {investor.relationship && (
              <p className="text-sm text-gray-500 mt-1">
                {RELATIONSHIP_LABELS[investor.relationship]}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={`/investors/${investor.id}/edit`}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
            >
              Remove
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <StatCard
            label="Capital Committed"
            value={fmtMoney(investor.total_capital_committed)}
          />
          <StatCard
            label="Capital Deployed"
            value={fmtMoney(totalDeployed)}
          />
          <StatCard label="Active Loans" value={activeCount.toString()} />
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Interest Received"
            value={fmtMoney(distribution_stats.total_interest_received)}
          />
          <StatCard
            label="Principal Returned"
            value={fmtMoney(distribution_stats.total_principal_returned)}
          />
          <StatCard
            label="Lifetime Distributed"
            value={fmtMoney(distribution_stats.total_distributed)}
          />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-1">Annual Statement</h2>
              <p className="text-xs text-gray-500 max-w-md">
                Download a PDF statement covering this investor's participations and distributions for the selected tax year.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statementYear}
                onChange={(e) => setStatementYear(Number(e.target.value))}
                className="text-xs px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-700"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleDownloadStatement}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                </svg>
                Download {statementYear} statement (PDF)
              </button>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            Informational only. LendSolo is a tracking tool, not a tax advisor. Consult your accountant.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Contact</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Field label="Email" value={investor.email} />
            <Field label="Phone" value={investor.phone} />
          </div>
          {investor.notes && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Notes</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{investor.notes}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-1">Investor Portal Access</h2>
              <p className="text-xs text-gray-500 max-w-md">
                Send a magic-link login so this investor can self-serve their participations, distributions, and statements.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                    investor.portal_enabled
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${investor.portal_enabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                  {investor.portal_enabled ? 'Portal enabled' : 'Portal disabled'}
                </span>
                <span className="text-[11px] text-gray-500">
                  {formatLastSignIn(investor.portal_last_signed_in_at)}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleInviteToPortal}
              disabled={!investor.email}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {investor.portal_enabled ? 'Re-send portal invite' : 'Invite to portal'}
            </button>
          </div>
          {!investor.email && (
            <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              Add an email address (Edit this investor) to enable portal invites.
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Distributions</h2>
            <p className="text-xs text-gray-500 mt-1">
              Pro-rata payouts from each payment, based on this investor's ownership share.
            </p>
          </div>
          {recent_distributions.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No distributions yet. They'll appear here once payments are recorded on loans this investor participates in.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Loan</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Borrower</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Interest</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Principal</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_distributions.map((d) => (
                    <tr
                      key={d.id}
                      className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => router.visit(`/loans/${d.loan_id}`)}
                    >
                      <td className="py-2.5 px-4 text-gray-600">{d.payment_date}</td>
                      <td className="py-2.5 px-4 font-medium text-emerald-600">#{d.loan_number}</td>
                      <td className="py-2.5 px-4 text-gray-700">{d.borrower_name}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-gray-900">{fmtMoney(d.interest_share)}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-gray-900">{fmtMoney(d.principal_share)}</td>
                      <td className="py-2.5 px-4 text-right font-mono font-semibold text-gray-900">{fmtMoney(d.total_share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Participations</h2>
              <p className="text-xs text-gray-500 mt-1">
                Loans this investor has capital in. Add or remove participations from each loan's page.
              </p>
            </div>
            {historical_participations.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                {showHistory ? 'Hide history' : `Show history (${historical_participations.length})`}
              </button>
            )}
          </div>
          {participations.length === 0 && !showHistory ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No active participations. Open a loan and add this investor from its page.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Loan</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Borrower</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Principal Share</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Ownership %</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Effective</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {participations.map((p) => {
                    const badge = LOAN_STATUS_BADGES[p.loan_status] || LOAN_STATUS_BADGES.active
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => router.visit(`/loans/${p.loan_id}`)}
                      >
                        <td className="py-2.5 px-4 font-medium text-emerald-600">#{p.loan_number}</td>
                        <td className="py-2.5 px-4 text-gray-700">{p.borrower_name}</td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                          ${p.principal_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                          {p.ownership_percent.toFixed(2)}%
                        </td>
                        <td className="py-2.5 px-4 text-xs text-gray-500">
                          {p.effective_from} &mdash; present
                        </td>
                        <td className="py-2.5 px-4">
                          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                  {showHistory && historical_participations.map((p) => {
                    return (
                      <tr
                        key={`hist-${p.id}`}
                        className="border-b border-gray-50 bg-gray-50/30 cursor-pointer"
                        onClick={() => router.visit(`/loans/${p.loan_id}`)}
                      >
                        <td className="py-2.5 px-4 font-medium text-gray-400">#{p.loan_number}</td>
                        <td className="py-2.5 px-4 text-gray-400">{p.borrower_name}</td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-400">
                          ${p.principal_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-400">
                          {p.ownership_percent.toFixed(2)}%
                        </td>
                        <td className="py-2.5 px-4 text-xs text-gray-400">
                          {p.effective_from} &mdash; {p.effective_until}
                        </td>
                        <td className="py-2.5 px-4">
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-500">
                            ended
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-bold font-mono text-gray-900">{value}</p>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-gray-900">{value || <span className="text-gray-300">—</span>}</p>
    </div>
  )
}
